import Highlighter from "react-highlight-words";
import { Link } from "react-router-dom";
import { CommandItem } from "cmdk";
import { format } from "date-fns";

import { Badge } from "@/components/ui/Badge";
import { variants } from "@/components/ui/Badge/utils";
import { Warning20 } from "@/components/ui/SvgIcon";
import { Tooltip } from "@/components/ui/Tooltip";

import { Trip, TripStageKey } from "@/features/trips/types";
import { Place } from "@/features/types";

import { UnitUtils } from "@/utils/unit";
import { dateFormat } from "@/constants/dateFormat";
import { ROUTES } from "@/constants/routes";
type Props = {
  trip: Trip;
  onSelect?: (to: string) => void;
  query?: string;
};
export const SearchItemTrip = ({
  trip,
  onSelect = () => {},
  query = "",
}: Props) => {
  const to = `${ROUTES.TRIPS}/${trip.tripId.chorusTripId}`;

  function getPlaceTitle(place: Place) {
    if (place.customerName) return place.customerName;
    const { latitudeMicro, longitudeMicro } = place.pointAndRadius.point;
    const lat = UnitUtils.fromMicro(latitudeMicro);
    const lng = UnitUtils.fromMicro(longitudeMicro);
    const title = `${lat}, ${lng}`;
    return title;
  }

  function renderCurrentStage(currentStage: TripStageKey) {
    const stageConfig: Record<
      string,
      { text: string; variant: keyof typeof variants }
    > = {
      NOT_STARTED: { text: "Not Started", variant: "gray" },
      PENDING_DEPARTURE: { text: "Pending Departure", variant: "sky" },
      IN_TRANSIT: { text: "In Transit", variant: "sky" },
      PENDING_ARRIVAL: { text: "Pending Arrival", variant: "sky" },
      COMPLETED: { text: "Completed", variant: "green" },
      CANCELED: { text: "Canceled", variant: "red" },
    };

    const { text, variant } = stageConfig[currentStage];

    return (
      <Badge className="font-manrope" variant={variant}>
        {text}
      </Badge>
    );
  }

  function renderTime(time: string) {
    return (
      <div className="truncate text-xs font-medium text-chorus-gray-800">
        {format(new Date(time), dateFormat.popupTimestamp)}
      </div>
    );
  }

  function renderTripInfo(
    place: Place,
    time?: string,
    isDestination?: boolean,
  ) {
    return (
      <div className="grid grid-cols-[1fr_auto] items-center justify-between gap-1">
        <div className="truncate text-xs font-medium text-chorus-gray-800">
          {isDestination ? "To" : "From"} {getPlaceTitle(place)}
        </div>
        {time ? renderTime(time) : "-"}
      </div>
    );
  }

  return (
    <CommandItem
      data-testid="search-item"
      onSelect={() => onSelect(to)}
      className="group relative cursor-pointer select-none items-center bg-white px-3 py-[2px] text-sm outline-none first:before:block first:before:h-[12px] first:before:w-full first:before:content-[''] last:after:block last:after:h-[12px] last:after:w-full last:after:content-['']   data-[disabled='true']:pointer-events-none data-[disabled='true']:opacity-50 [&:not(:last-child)]:mb-2"
    >
      <Link
        to={to}
        className="flex h-full min-h-[56px] items-start rounded-md py-[7px] pl-3 pr-2 group-aria-selected:bg-[#F8FAFC]"
      >
        <div className="flex flex-1 flex-col gap-1 overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] items-center justify-between gap-1 font-sora text-sm font-medium tracking-[-0.003em] text-chorus-black-200">
            <Highlighter
              className="truncate"
              highlightClassName=" bg-chorus-teal-100 rounded-[2px] text-chorus-black-100"
              searchWords={query?.split(" ")}
              autoEscape={true}
              textToHighlight={trip.tripId.customerTripId}
            />
            <div className="flex gap-[6px]">
              {renderCurrentStage(trip.currentStage)}
              {trip.hasUnresolvedAlert && (
                <Tooltip
                  content="Show Alerts"
                  placement={"top-end"}
                  className="w-fit"
                >
                  <div
                    data-testid="search-item-device-unresolved-alert"
                    className=" flex h-[20px] w-[24px]  items-center justify-center rounded-[20px] bg-chorus-red-100 pb-[1.7px] "
                  >
                    <Warning20 className="w-[17px]  fill-chorus-red-600" />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          {trip.origin && renderTripInfo(trip.origin, trip.startTime, false)}
          {trip.destination &&
            renderTripInfo(trip.destination, trip.endTime, true)}
        </div>
      </Link>
    </CommandItem>
  );
};
